import React from 'react';
import Button from '../Button';
import { Table, BarChart2, LineChart, PieChart, Settings, X, CornerRightDown, Layers, RefreshCw, Grid, TrendingUp } from 'lucide-react';

const formatLargeNumber = (num) => {
  const roundedNum = parseFloat(num.toFixed(2));
  if (roundedNum >= 1000000000) {
    return (roundedNum / 1000000000).toFixed(2).replace(/\.?0+$/, '') + 'B';
  }
  if (roundedNum >= 1000000) {
    return (roundedNum / 1000000).toFixed(2).replace(/\.?0+$/, '') + 'M';
  }
  if (roundedNum >= 1000) {
    return (roundedNum / 1000).toFixed(2).replace(/\.?0+$/, '') + 'K';
  }
  return roundedNum.toString();
};

const formatLabel = (label) => {
  if (!label || typeof label !== 'string') {
    return '';
  }
  return label
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const AlignedButtonLayout = ({
  viewMode,
  setViewMode,
  chartType,
  setChartType,
  canShowGraph,
  isSingleValue,
  hasResults,
  isMobile,
  isDashboardMode,
  isEditMode,
  toggleEditMode,
  statsData,
  yAxisLabel,
  isNumberFormatted,
  onNumberFormatToggle,
  showFormatCheckbox,
  isTransposed,
  // New transformation-related props
  transformationType,
  onTransformationChange,
  isTransforming,
  // Trend line related props
  showTrendLine,
  onToggleTrendLine
}) => {
  const handleViewModeChange = (newMode) => {
    setViewMode(newMode);
    if (newMode !== 'table') {
      setChartType(newMode);
    }
  };

  const shouldUseEditButton = isDashboardMode;

  // Dropdown state for transform options
  const [showTransformDropdown, setShowTransformDropdown] = React.useState(false);
  const [isTransformButtonHovered, setIsTransformButtonHovered] = React.useState(false);
  const dropdownRef = React.useRef(null);

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowTransformDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const renderControls = () => (
    <>
      {showFormatCheckbox && !isTransposed && (
        <div className="flex items-center space-x-2 mr-2">
          <input
            type="checkbox"
            id="number-format"
            checked={isNumberFormatted}
            onChange={(e) => onNumberFormatToggle(e.target.checked)}
            className="form-checkbox h-4 w-4 border-gray-300 text-gray-500 focus:ring-blue-500"
          />
          <label
            htmlFor="number-format"
            className="text-sm font-medium leading-none text-gray-700 select-none"
            onClick={(e) => e.preventDefault()}
          >
            K/M/B
          </label>
        </div>
      )}
      {showFormatCheckbox && isTransposed && (
        <div className="flex items-center space-x-2 mr-2">
          <input
            type="checkbox"
            id="number-format"
            checked={isNumberFormatted}
            onChange={(e) => onNumberFormatToggle(e.target.checked)}
            className="form-checkbox h-4 w-4 border-gray-300 text-gray-500 focus:ring-blue-500"
          />
          <label
            htmlFor="number-format"
            className="text-sm font-medium leading-none text-gray-700 select-none"
            onClick={(e) => e.preventDefault()}
          >
            Formatted
          </label>
        </div>
      )}
      
      {/* Transform Button and Dropdown - Available in both dashboard and regular mode */}
      {viewMode === 'table' && (canShowGraph || transformationType) && (
        <div 
          className="relative mr-2" 
          ref={dropdownRef}
          onMouseEnter={() => transformationType && setIsTransformButtonHovered(true)}
          onMouseLeave={() => setIsTransformButtonHovered(false)}
        >
          <Button
            onClick={() => {
              // If transformation is active, clicking the button reverts to original
              if (transformationType) {
                onTransformationChange(null);
              } else {
                setShowTransformDropdown(!showTransformDropdown);
              }
            }}
            icon={Layers}
            className={`text-sm h-9 px-2 sm:px-3 py-2 relative ${
              transformationType ? 'text-primary font-medium bg-blue-50' : ''
            }`}
            disabled={isTransforming}
          >
            <span className="hidden sm:inline">
              {transformationType ? 
                (transformationType === 'transpose' ? 'Transposed' : 
                transformationType === 'pivot' ? 'Pivoted' : 'Transform') : 
                'Transform'}
            </span>
            {isTransforming && (
              <RefreshCw className="ml-1 w-4 h-4 animate-spin" />
            )}
            
            {/* X button that appears on hover */}
            {isTransformButtonHovered && transformationType && (
              <span 
                className="ml-1.5 text-gray-500 hover:text-red-500 group-hover:opacity-100 cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  onTransformationChange(null);
                }}
                title="Revert transformation"
              >
                ×
              </span>
            )}
          </Button>
          
          {showTransformDropdown && !transformationType && (
            <div className="absolute z-50 mt-1 right-0 bg-white rounded-md shadow-lg py-1 w-40 border border-gray-200">
             <button
                className={`w-full text-left px-4 py-2 text-sm flex items-center ${
                  transformationType === 'transpose' ? 'bg-blue-50 text-blue-700 font-medium' : 
                  transformationType === 'pivot' ? 'opacity-50 cursor-not-allowed text-gray-400' : 'hover:bg-gray-100'
                }`}
                onClick={() => {
                  if (transformationType !== 'pivot') {
                    onTransformationChange('transpose');
                    setShowTransformDropdown(false);
                  }
                }}
                disabled={transformationType === 'pivot'}
              >
                <CornerRightDown className="mr-2 h-4 w-4" />
                Transpose
              </button>
              <button
                className={`w-full text-left px-4 py-2 text-sm flex items-center ${
                  transformationType === 'pivot' ? 'bg-blue-50 text-blue-700 font-medium' : 
                  transformationType === 'transpose' ? 'opacity-50 cursor-not-allowed text-gray-400' : 'hover:bg-gray-100'
                }`}
                onClick={() => {
                  if (transformationType !== 'transpose') {
                    onTransformationChange('pivot');
                    setShowTransformDropdown(false);
                  }
                }}
                disabled={transformationType === 'transpose'}
              >
                <Grid className="mr-2 h-4 w-4" />
                Pivot
              </button>
              {transformationType && (
                <button
                  className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 text-gray-700 flex items-center"
                  onClick={() => {
                    onTransformationChange(null);
                    setShowTransformDropdown(false);
                  }}
                >
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Original View
                </button>
              )}
            </div>
          )}
        </div>
      )}
      
      {/* Visual separator between transform and view type buttons */}
      {viewMode === 'table' && canShowGraph && (
        <div className="h-8 border-r border-gray-300 mx-2"></div>
      )}
      
      {canShowGraph && (
        <div className="flex items-center">
          {viewMode === 'table' ? (
            // When in table view, show Chart button on left, Table on right
            <>
              {/* Chart Button */}
              <Button
                onClick={() => handleViewModeChange('bar')}
                icon={BarChart2}
                className="text-sm h-9 px-2 sm:px-3 py-2 border border-gray-300 rounded-md mr-2"
              >
                <span className="hidden sm:inline">Chart</span>
              </Button>
              
              {/* Table Button - on the right */}
              <Button
                onClick={() => handleViewModeChange('table')}
                icon={Table}
                className="text-sm h-9 px-2 sm:px-3 py-2 border rounded-md bg-blue-100 border-blue-300"
              >
                <span className="hidden sm:inline">Table</span>
              </Button>
            </>
          ) : (
            // When in chart view, show chart buttons on left, table on right
            <>
              {/* Trend line toggle - now on the left of chart buttons */}
              <div className="flex items-center space-x-2 mr-2">
                <input
                  type="checkbox"
                  id="trend-line"
                  checked={showTrendLine}
                  onChange={() => typeof onToggleTrendLine === 'function' && onToggleTrendLine(!showTrendLine)}
                  className="form-checkbox h-4 w-4 border-gray-300 text-primary-600 focus:ring-blue-500"
                />
                <label
                  htmlFor="trend-line"
                  className="text-sm font-medium leading-none text-gray-700 select-none flex items-center"
                  title={showTrendLine ? "Hide trend line" : "Show trend line"}
                >
                  <TrendingUp className="h-4 w-4 mr-1" />
                  <span className="hidden sm:inline">Trend</span>
                </label>
              </div>

              {/* Grouped chart buttons */}
              <div className="flex overflow-hidden rounded-md mr-2">
                <Button
                  onClick={() => handleViewModeChange('pie')}
                  icon={PieChart}
                  className={`text-sm h-9 px-2 sm:px-3 py-2 border ${viewMode === 'pie' ? 'bg-blue-100 border-blue-300' : 'border-gray-300'} ${viewMode !== 'pie' ? 'border-r-0' : ''}`}
                >
                  <span className="hidden sm:inline">Pie</span>
                </Button>
                <Button
                  onClick={() => handleViewModeChange('line')}
                  icon={LineChart}
                  className={`text-sm h-9 px-2 sm:px-3 py-2 border ${viewMode === 'line' ? 'bg-blue-100 border-blue-300' : 'border-gray-300'} ${viewMode !== 'line' ? 'border-r-0' : ''}`}
                >
                  <span className="hidden sm:inline">Line</span>
                </Button>
                <Button
                  onClick={() => handleViewModeChange('bar')}
                  icon={BarChart2}
                  className={`text-sm h-9 px-2 sm:px-3 py-2 border ${viewMode === 'bar' ? 'bg-blue-100 border-blue-300' : 'border-gray-300'}`}
                >
                  <span className="hidden sm:inline">Bar</span>
                </Button>
              </div>
              
              {/* Table Button - on the right */}
              <Button
                onClick={() => handleViewModeChange('table')}
                icon={Table}
                className="text-sm h-9 px-2 sm:px-3 py-2 border border-gray-300 rounded-md"
              >
                <span className="hidden sm:inline">Table</span>
              </Button>
            </>
          )}
        </div>
      )}
    </>
  );

  return (
    <div className={`flex flex-col space-y-4 ${isEditMode ? 'bg-gray-50 rounded-md p-2' : ''}`}>
      <div className="flex flex-col items-end sm:flex-row sm:justify-end sm:items-center space-y-2 sm:space-y-0 sm:relative sm:min-h-[40px]">
       {!isEditMode && viewMode !== 'table' && statsData && (
        <div className="hidden sm:flex bg-gray-100 rounded-md px-4 py-2 flex-col items-start self-start sm:absolute sm:left-0 sm:top-0">  
            <span className="text-xs text-gray-500 mb-1">{formatLabel(Array.isArray(yAxisLabel) ? yAxisLabel[0] : yAxisLabel)}</span>
            <div className="flex items-center space-x-4 text-sm text-gray-600">
              <span><strong>Sum:</strong> {formatLargeNumber(parseFloat(statsData.sum))}</span>
              <span><strong>Avg:</strong> {formatLargeNumber(parseFloat(statsData.avg))}</span>
            </div>
          </div>
        )}
        <div className="flex items-center space-x-2">
          {shouldUseEditButton ? (
            <Button
              onClick={toggleEditMode}
              icon={isEditMode ? X : Settings}
              className={`text-sm h-9 px-2 sm:px-3 py-2 ${
                isEditMode
                  ? 'bg-gray-700 text-white font-semibold'
                  : 'text-gray-700'
              } rounded-md transition-all duration-200`}
              title={isMobile ? undefined : (isEditMode ? 'Close Edit' : 'Edit')}
            >
              <span className="hidden sm:inline">{isEditMode ? 'Close Edit' : 'Edit'}</span>
            </Button>
          ) : null}
          {((!shouldUseEditButton) || (shouldUseEditButton && isEditMode)) && renderControls()}
        </div>
      </div>
      
      {/* Transformation status indicator */}
      {transformationType && !isTransforming && (
        <div className="text-xs text-gray-500 flex items-center">
          <span className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full font-medium flex items-center">
            {transformationType === 'transpose' ? (
              <><CornerRightDown className="mr-1 h-3 w-3" /> Transposed View</>
            ) : (
              <><Grid className="mr-1 h-3 w-3" /> Pivoted View</>
            )}
          </span>
        </div>
      )}
    </div>
  );
};

export default AlignedButtonLayout;